<script setup lang="ts">
import { onScopeDispose } from 'vue'
import { authContextEpoch, onAuthContextChanged } from '@/@vxapp/Auth/authContext'
import { useAuthStore } from '@/@vxapp/Auth/useAuthStore'
import { Action as A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { Subject as S } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { nextTick, onMounted, ref, shallowRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useTemplatesStore } from '@/@vxapp/Messages/Templates/useTemplatesStore'
import { useAppNotifications } from '@/plugins/toast/composables/useAppNotifications'
import TemplateDialog from '@/components/Dialogs/Templates/TemplateDialog.vue'
import TemplatesDataTable from '@/components/Tables/Messages/Templates/TemplatesDataTable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type TemplateData = Record<string, unknown>
type DisplayType = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]

definePage({ meta: { ...PageMeta.Templates.List.Meta, pageTitle: PageMeta.Templates.List.Title } })

const log = createLogger('TemplatesListPage')
const route = useRoute()
const router = useRouter()
const templatesStore = useTemplatesStore() as any
const notify = useAppNotifications()
const templatesTable = ref<InstanceType<typeof TemplatesDataTable> | null>(null)
const templateData = ref<TemplateData | null>(null)
const dialogDisplayType = shallowRef<DisplayType>(DialogDisplayType.CREATE)
const isDialogVisible = shallowRef(false)
const isRouteReady = shallowRef(false)
const searchQuery = ref('')
const resultCount = ref(0)
const defaults: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+templateName' }
const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Template name A–Z', value: '+templateName' },
  { title: 'Template name Z–A', value: '-templateName' },
]

function stringValue(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}

function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(stringValue(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function normalize(source: Record<string, unknown>): PaginationQueryParams {
  const requestedLimit = positiveInteger(source.limit, defaults.limit)
  const sort = stringValue(source.sort)
  const search = stringValue(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(requestedLimit) ? requestedLimit : defaults.limit,
    sort: sort === '-templateName' ? '-templateName' : '+templateName',
    search: search || undefined,
  }
}

function same(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRoute() {
  const current = normalize(templatesStore.queryParams ?? defaults)
  const incoming = normalize(route.query as Record<string, unknown>)

  searchQuery.value = incoming.search ?? ''
  if (same(current, incoming)) return false
  templatesStore.queryParams = incoming

  return true
}

function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}

async function fetchTemplates() {
  await templatesTable.value?.fetchTemplates()
}

async function updateQuery(patch: Partial<PaginationQueryParams>) {
  const next = normalize({ ...templatesStore.queryParams, ...patch })

  templatesStore.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchTemplates()
}

async function handleSearch() {
  await updateQuery({ search: searchQuery.value.trim() || undefined, page: 1 })
}

function handleSearchInput(value: unknown) {
  searchQuery.value = String(value ?? '')
}

async function handleClear() {
  searchQuery.value = ''
  await updateQuery({ search: undefined, page: 1 })
}

async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value), page: 1 })
}

async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}

async function handlePage(page: number) {
  await updateQuery({ page })
}

function handleLoaded(count: number) {
  resultCount.value = count
}

async function handleCreateTemplate() {
  const requestEpoch = authContextEpoch()
  const { template, responseCode } = await templatesStore.fetchTemplate()

  if (requestEpoch !== authContextEpoch()) return

  if (responseCode !== ResponseCode.OK) return
  templateData.value = cloneReactiveData(template ?? {})
  dialogDisplayType.value = DialogDisplayType.CREATE
  isDialogVisible.value = true
}

async function handleEdit(templateGUID: string) {
  const requestEpoch = authContextEpoch()
  const { template, responseCode } = await templatesStore.fetch(templateGUID)

  if (requestEpoch !== authContextEpoch()) return

  if (responseCode !== ResponseCode.OK) {
    log.error('Failed to load template for editing', { templateGUID, responseCode })

    return
  }

  templateData.value = cloneReactiveData(template ?? {})
  dialogDisplayType.value = DialogDisplayType.EDIT
  isDialogVisible.value = true
}

async function handleSave(payload: TemplateData) {
  const response =
    dialogDisplayType.value === DialogDisplayType.CREATE
      ? await templatesStore.create(payload)
      : await templatesStore.update(payload)

  if (![ResponseCode.OK, ResponseCode.CREATED].includes(response.responseCode)) {
    log.error('Failed to save message template', response)
    notify.error(response.message ?? 'Failed to save message template')

    return
  }

  isDialogVisible.value = false
  await fetchTemplates()
}

async function handlePreview(templateGUID: string) {
  await router.push({
    name: PageMeta.Templates.Preview.Name as 'setup-emails-templates-preview-guid',
    params: { guid: templateGUID },
  })
}

templatesStore.queryParams = { ...defaults }
applyRoute()
isRouteReady.value = true
onMounted(fetchTemplates)

watch(
  () => route.query,
  async () => {
    if (!isRouteReady.value || !applyRoute()) return
    await nextTick()
    await fetchTemplates()
  },
  { deep: true }
)

const canReadCount = () =>
  useAuthStore()
    .getAbility()
    ?.can(A.READ, S.MESSAGES_TEMPLATES_PREVIEW_ALL, 'paginationMeta.entriesFound') ?? false

onScopeDispose(
  onAuthContextChanged(() => {
    resultCount.value = 0
    isDialogVisible.value = false
    templateData.value = null
  })
)
</script>

<template>
  <main
    class="templates-workspace"
    data-testid="templates-workspace"
  >
    <section class="templates-workspace__hero">
      <div>
        <span>Structured content / message delivery</span>
        <h1>Compose once.<br />Publish everywhere.</h1>
        <p>Shape reusable subjects, copy and delivery rules from a focused editorial studio.</p>
      </div>
      <aside>
        <code>CONTENT LAKE</code>
        <strong v-if="canReadCount()">{{ resultCount }}</strong>
        <span>indexed documents</span>
        <VBtn @click="handleCreateTemplate">Create template</VBtn>
      </aside>
    </section>

    <VCard class="templates-workspace__toolbar">
      <VCardText>
        <VForm
          class="templates-workspace__search"
          role="search"
          @submit.prevent="handleSearch"
        >
          <!-- vortex-validation-ignore-element: server search is non-validation filter UI -->
          <AppTextField
            :model-value="searchQuery"
            aria-label="Search message templates"
            clearable
            hide-details
            placeholder="Search content documents"
            vortex-validation-ignore-element
            @click:clear="handleClear"
            @update:model-value="handleSearchInput"
          />
          <VBtn
            type="button"
            @click="handleSearch"
            >Search</VBtn
          >
        </VForm>
        <!-- vortex-validation-ignore-element: result sorting is non-validation UI -->
        <AppSelect
          :model-value="templatesStore.queryParams?.sort ?? defaults.sort"
          :items="sortItems"
          aria-label="Sort message templates"
          hide-details
          label="Sort"
          vortex-validation-ignore-element
          @update:model-value="handleSort"
        />
        <!-- vortex-validation-ignore-element: page size is non-validation UI -->
        <AppSelect
          :model-value="templatesStore.queryParams?.limit ?? defaults.limit"
          :items="limitItems"
          aria-label="Message templates per page"
          hide-details
          label="Per page"
          vortex-validation-ignore-element
          @update:model-value="handleLimit"
        />
      </VCardText>
    </VCard>

    <TemplatesDataTable
      ref="templatesTable"
      @edit="handleEdit"
      @loaded="handleLoaded"
      @page-change="handlePage"
      @preview="handlePreview"
    />
  </main>

  <TemplateDialog
    v-if="templateData"
    v-model:is-dialog-visible="isDialogVisible"
    :display-type="dialogDisplayType"
    :template-data="templateData"
    @create="handleSave"
    @update="handleSave"
  />
</template>

<style scoped lang="scss">
.templates-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1rem;
  font-family: 'Space Grotesk', Inter, system-ui, sans-serif;
}

.templates-workspace__hero {
  display: grid;
  min-block-size: 27rem;
  align-items: end;
  border: 1px solid #212121;
  border-radius: 0.75rem;
  background:
    linear-gradient(90deg, transparent 49.8%, rgba(255, 255, 255, 0.04) 50%),
    linear-gradient(#0b0b0b, #0b0b0b);
  color: #fff;
  gap: clamp(2rem, 8vw, 8rem);
  grid-template-columns: minmax(0, 1fr) minmax(12rem, 0.3fr);
  padding: clamp(2rem, 6vw, 5rem);
}

.templates-workspace__hero > div > span,
.templates-workspace__hero code,
.templates-workspace__hero aside > span {
  color: #797979;
  font-family: 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7rem;
  text-transform: uppercase;
}

.templates-workspace__hero h1 {
  max-inline-size: 12ch;
  margin: 1.25rem 0;
  color: #fff;
  font-size: clamp(3rem, 7vw, 6rem);
  font-weight: 400;
  letter-spacing: -0.045em;
  line-height: 0.98;
}

.templates-workspace__hero p {
  max-inline-size: 52ch;
  margin: 0;
  color: #b9b9b9;
  font-size: 1.05rem;
}

.templates-workspace__hero aside {
  display: grid;
  align-content: end;
  border-inline-start: 1px solid #353535;
  gap: 0.5rem;
  padding-inline-start: clamp(1.5rem, 4vw, 3rem);
}

.templates-workspace__hero aside strong {
  color: #19d600;
  font-size: clamp(3rem, 7vw, 5rem);
  font-weight: 400;
  line-height: 1;
}

.templates-workspace__hero :deep(.v-btn) {
  min-block-size: 2.75rem;
  margin-block-start: 1rem;
  border-radius: 9999px;
  background: #f36458;
  color: #fff;
}

.templates-workspace__hero :deep(.v-btn:hover) {
  background: #0052ef;
}

.templates-workspace__toolbar {
  border: 1px solid #353535;
  border-radius: 0.375rem;
  background: #212121;
}

.templates-workspace__toolbar :deep(.v-card-text) {
  display: grid;
  align-items: center;
  gap: 0.75rem;
  grid-template-columns: minmax(16rem, 1fr) minmax(10rem, 0.3fr) minmax(8rem, 0.2fr);
  padding: 0.75rem;
}

.templates-workspace__search {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.templates-workspace__toolbar :deep(.v-field) {
  border-radius: 0.1875rem;
  background: #0b0b0b;
}

.templates-workspace__toolbar :deep(.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 9999px;
}

@media (max-width: 799px) {
  .templates-workspace__hero {
    min-block-size: 32rem;
    grid-template-columns: 1fr;
  }

  .templates-workspace__hero aside {
    border-block-start: 1px solid #353535;
    border-inline-start: 0;
    padding-block-start: 1.5rem;
    padding-inline-start: 0;
  }

  .templates-workspace__toolbar :deep(.v-card-text) {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 420px) {
  .templates-workspace__hero {
    padding: 1.5rem;
  }

  .templates-workspace__hero h1 {
    overflow-wrap: anywhere;
    font-size: clamp(2rem, 10vw, 2.65rem);
  }

  .templates-workspace__search {
    grid-template-columns: 1fr;
  }
}
</style>
